/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { Link } from '@tanstack/react-router'
import type { ComponentProps, ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface BringTheHeatProps {
  heading?: ReactNode
  ctaLabel?: ReactNode
  ctaTo?: string
  rootProps?: ComponentProps<'section'>
  headingProps?: ComponentProps<'h2'>
  ctaButtonProps?: Omit<ComponentProps<typeof Button>, 'children' | 'render' | 'nativeButton'>
  textureImageProps?: Omit<ComponentProps<'img'>, 'src' | 'alt'>
}

const DEFAULT_HEADING = 'READY TO BRING THE HEAT?'
const DEFAULT_CTA_LABEL = 'JOIN THE CLUB'
const DEFAULT_TEXTURE_IMAGE_ALT = 'Textured grainy close up of red flames and sparks on dark background'

export function BringTheHeat({
  heading = DEFAULT_HEADING,
  ctaLabel = DEFAULT_CTA_LABEL,
  ctaTo = '/auth/signup',
  rootProps,
  headingProps,
  ctaButtonProps,
  textureImageProps,
}: BringTheHeatProps) {
  const { className: rootClassName, ...rootRest } = rootProps ?? {}
  const { className: headingClassName, ...headingRest } = headingProps ?? {}
  const { className: ctaClassName, ...ctaRest } = ctaButtonProps ?? {}
  const { className: textureClassName, ...textureRest } = textureImageProps ?? {}

  return (
    <section
      className={cn(
        'relative w-full overflow-hidden rounded-[2rem] bg-gradient-to-br from-red-600 to-red-700 p-10 text-center md:p-12',
        rootClassName
      )}
      {...rootRest}
    >
      <div className="relative z-10 flex flex-col gap-6">
        <h2
          className={cn('font-black text-3xl text-white uppercase leading-tight md:text-4xl', headingClassName)}
          {...headingRest}
        >
          {heading}
        </h2>
        <div className="flex justify-center">
          <Button
            className={cn(
              'rounded-xl bg-white px-10 py-7 font-black text-red-700 text-sm uppercase shadow-xl/50 transition-transform hover:scale-105 hover:bg-white md:text-base',
              ctaClassName
            )}
            nativeButton={false}
            render={<Link to={ctaTo} />}
            {...ctaRest}
          >
            {ctaLabel}
          </Button>
        </div>
      </div>
      <div className="pointer-events-none absolute inset-0 opacity-20">
        <picture className="block h-full w-full">
          <source srcSet="/intro-ready-heat-bg.avif" type="image/avif" />
          <source srcSet="/intro-ready-heat-bg.webp" type="image/webp" />
          <img
            alt={DEFAULT_TEXTURE_IMAGE_ALT}
            className={cn('h-full w-full object-cover', textureClassName)}
            decoding="async"
            height={209}
            src="/intro-ready-heat-bg.webp"
            width={412}
            {...textureRest}
          />
        </picture>
      </div>
    </section>
  )
}
